import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, waLink, formatErr } from "@/lib/api";
import { MapPin, MessageCircle, Heart, GraduationCap, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export function Visit() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
      <p className="text-xs uppercase tracking-[0.25em] font-bold text-turaco mb-3">Vizitează-ne</p>
      <h1 className="font-serif text-4xl lg:text-6xl text-forest-900 tracking-tight">Pregătește întâlnirea cu Habitatul Meu</h1>
      <p className="mt-4 text-muted">Pentru ca animalele să fie protejate, iar fiecare grup să aibă parte de o experiență apropiată și liniștită, accesul se face numai în cadrul tururilor programate.</p>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
        {[
          { t: "Tarif", d: "40 RON / persoană (adulți și copii peste 3 ani). Gratuit: 0–3 ani și copii cu dizabilități." },
          { t: "Durată", d: "Aproximativ 60 de minute per tur ghidat." },
          { t: "Ce include", d: "Turul ghidat, prezentarea animalelor, curiozități, interacțiune controlată și hrana permisă oferită de parc." },
          { t: "Rezervare", d: "Doar în avans, prin site sau WhatsApp. Locurile sunt limitate." },
        ].map((s, i) => (
          <div key={i} className="bg-cream-50 rounded-3xl p-6">
            <div className="text-xs uppercase tracking-wider font-bold text-turaco">{s.t}</div>
            <div className="mt-2 text-forest-900">{s.d}</div>
          </div>
        ))}
      </div>

      <div className="mt-10 bg-forest-900 text-cream-50 rounded-[32px] p-8">
        <h2 className="font-serif text-2xl mb-4">Înainte să vii</h2>
        <ul className="space-y-2">
          {["Verifică data și ora confirmate.", "Ajungi cu 10–15 minute înainte.", "Poartă încălțăminte potrivită pentru exterior.", "Supraveghează copiii pe tot parcursul vizitei.", "Oferă animalelor numai hrana aprobată de ghid.", "Respectă indicațiile ghidului.", "Evită blițul și zgomotele puternice în zonele indicate."].map((s, i) => (
            <li key={i} className="flex items-start gap-2"><Check className="w-4 h-4 text-tucan mt-1 shrink-0" /> {s}</li>
          ))}
        </ul>
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/tururi" className="bg-tucan text-white rounded-full px-6 py-3 font-semibold">Vezi tururile</Link>
        <a href={waLink()} target="_blank" rel="noreferrer" className="bg-[#25D366] text-white rounded-full px-6 py-3 font-semibold flex items-center gap-2"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
      </div>
    </div>
  );
}

export function Story() {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
      <p className="text-xs uppercase tracking-[0.25em] font-bold text-turaco mb-3">Povestea noastră</p>
      <h1 className="font-serif text-4xl lg:text-6xl text-forest-900 tracking-tight">Un loc crescut din pasiune, grijă și mii de ore petrecute alături de animale.</h1>
      <div className="mt-10 space-y-6 text-lg text-forest-900 leading-relaxed">
        <p><span className="font-serif text-6xl float-left mr-3 leading-none text-tucan">H</span>abitatul Meu este o asociație din Dumești, județul Iași, dedicată apropierii oamenilor de lumea păsărilor exotice și a animalelor. Gabriel și Irina îi întâmpină pe vizitatori și transformă fiecare tur într-o întâlnire personală, nu într-o simplă plimbare.</p>
        <p>Din 2018, când asociația a fost înființată, echipa a crescut Habitatul din pasiune pentru păsări și pentru natură. Fiecare animal are un nume, o poveste și o rutină bine adaptată nevoilor lui.</p>
        <p className="text-sm text-muted italic">Această secțiune va fi completată în curând cu poveștile fondatorilor. Conținut demonstrativ — verificare necesară înainte de publicare.</p>
      </div>
      <div className="mt-10 flex gap-3">
        <Link to="/tururi" className="bg-tucan text-white rounded-full px-6 py-3 font-semibold">Vezi tururile</Link>
        <Link to="/sustine-ne" className="border-2 border-forest-900 text-forest-900 rounded-full px-6 py-3 font-semibold">Susține-ne</Link>
      </div>
    </div>
  );
}

export function Schools() {
  const [form, setForm] = useState({
    institution: "", institution_type: "scoala", contact_name: "", role: "", phone: "", email: "",
    county: "Iași", locality: "", preferred_dates: "", kids_estimate: 0, ages: "", chaperones: 0,
    transport: "", accessibility: "", wants_offer: false, message: "", consent: false,
  });
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/schools/request", form); setSent(true); toast.success("Solicitare trimisă!"); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  if (sent) return (
    <div className="max-w-2xl mx-auto px-4 py-16 text-center">
      <div className="w-16 h-16 rounded-full bg-leaf/30 flex items-center justify-center mx-auto mb-4"><Check className="w-8 h-8 text-forest-900" /></div>
      <h1 className="font-serif text-3xl text-forest-900">Solicitarea a ajuns la noi</h1>
      <p className="mt-3 text-muted">Vom verifica disponibilitatea și vom reveni folosind datele de contact transmise.</p>
      <Link to="/" className="mt-6 inline-block text-forest-900 font-semibold underline">Înapoi la pagina principală</Link>
    </div>
  );
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
      <p className="text-xs uppercase tracking-[0.25em] font-bold text-turaco mb-3 flex items-center gap-2"><GraduationCap className="w-4 h-4" /> Școli și grupuri</p>
      <h1 className="font-serif text-4xl lg:text-6xl text-forest-900 tracking-tight">Natura nu se învață doar din manual. Se întâlnește.</h1>
      <p className="mt-4 text-muted">Grădinițe, școli, licee, Școala Altfel, Săptămâna Verde — trimite-ne o solicitare și revenim cu detalii.</p>
      <form onSubmit={submit} className="mt-10 bg-cream-50 rounded-[24px] p-6 lg:p-8 space-y-3">
        <input required data-testid="school-institution" placeholder="Instituție *" value={form.institution} onChange={e => setForm({...form, institution: e.target.value})} className="w-full rounded-2xl border border-forest-900/20 px-4 py-3 bg-white" />
        <select value={form.institution_type} onChange={e => setForm({...form, institution_type: e.target.value})} className="w-full rounded-2xl border border-forest-900/20 px-4 py-3 bg-white">
          <option value="gradinita">Grădiniță</option><option value="scoala">Școală primară</option><option value="gimnaziu">Gimnaziu</option><option value="liceu">Liceu</option><option value="afterschool">Afterschool / privat</option>
        </select>
        <input required data-testid="school-contact" placeholder="Persoană de contact *" value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} className="w-full rounded-2xl border border-forest-900/20 px-4 py-3 bg-white" />
        <div className="grid grid-cols-2 gap-3">
          <input required placeholder="Telefon *" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="rounded-2xl border border-forest-900/20 px-4 py-3 bg-white" />
          <input placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="rounded-2xl border border-forest-900/20 px-4 py-3 bg-white" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input placeholder="Localitate" value={form.locality} onChange={e => setForm({...form, locality: e.target.value})} className="rounded-2xl border border-forest-900/20 px-4 py-3 bg-white" />
          <input placeholder="Perioadă preferată" value={form.preferred_dates} onChange={e => setForm({...form, preferred_dates: e.target.value})} className="rounded-2xl border border-forest-900/20 px-4 py-3 bg-white" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <input type="number" placeholder="Nr. copii" value={form.kids_estimate} onChange={e => setForm({...form, kids_estimate: parseInt(e.target.value||0)})} className="rounded-2xl border border-forest-900/20 px-4 py-3 bg-white" />
          <input placeholder="Vârste" value={form.ages} onChange={e => setForm({...form, ages: e.target.value})} className="rounded-2xl border border-forest-900/20 px-4 py-3 bg-white" />
          <input type="number" placeholder="Însoțitori" value={form.chaperones} onChange={e => setForm({...form, chaperones: parseInt(e.target.value||0)})} className="rounded-2xl border border-forest-900/20 px-4 py-3 bg-white" />
        </div>
        <textarea placeholder="Detalii, transport, nevoi speciale" value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="w-full rounded-2xl border border-forest-900/20 px-4 py-3 bg-white min-h-[100px]" />
        <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={form.consent} onChange={e => setForm({...form, consent: e.target.checked})} /> Sunt de acord cu politica de confidențialitate (GDPR) *</label>
        <button data-testid="school-submit" disabled={!form.consent} className="w-full bg-tucan text-white rounded-full py-3 font-semibold disabled:opacity-40">Trimite solicitarea</button>
      </form>
    </div>
  );
}

export function Donate() {
  const [amount, setAmount] = useState(50);
  const [form, setForm] = useState({ donor_name: "", email: "", anonymous: false, message: "", public_name_consent: false });
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/donations", { ...form, amount: parseFloat(amount) });
      toast.info(data.message);
      setSent(true);
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
      <p className="text-xs uppercase tracking-[0.25em] font-bold text-tucan mb-3 flex items-center gap-2"><Heart className="w-4 h-4" /> Susține-ne</p>
      <h1 className="font-serif text-4xl lg:text-6xl text-forest-900 tracking-tight">Fiecare contribuție susține o poveste vie.</h1>
      <p className="mt-4 text-muted">Hrana, îngrijirea și amenajarea habitatelor presupun muncă zilnică. Poți susține Habitatul printr-o donație sau adopție simbolică.</p>
      {sent ? (
        <div className="mt-8 bg-cream-50 rounded-3xl p-8">
          <p className="text-forest-900">Mulțumim pentru intenție! Plățile online vor fi disponibile în curând — pentru a finaliza acum donația, scrie-ne pe WhatsApp și îți transmitem detaliile bancare.</p>
          <a href={waLink(`Doresc să donez ${amount} RON către Habitatul Meu.`)} target="_blank" rel="noreferrer" className="mt-4 inline-flex bg-[#25D366] text-white rounded-full px-6 py-3 font-semibold items-center gap-2"><MessageCircle className="w-4 h-4" /> Contactează-ne</a>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-10 bg-cream-50 rounded-[24px] p-6 lg:p-8 space-y-4">
          <div>
            <label className="text-sm font-semibold text-forest-900">Sumă (RON)</label>
            <div className="flex gap-2 mt-2 flex-wrap">
              {[20, 50, 100, 200, 500].map(v => <button type="button" key={v} onClick={() => setAmount(v)} className={`px-4 py-2 rounded-full font-semibold ${amount === v ? "bg-forest-900 text-cream-50" : "bg-white border border-forest-900/20 text-forest-900"}`}>{v}</button>)}
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)} className="rounded-full border border-forest-900/20 px-4 py-2 bg-white w-24" />
            </div>
          </div>
          <input placeholder="Nume (opțional)" value={form.donor_name} onChange={e => setForm({...form, donor_name: e.target.value})} className="w-full rounded-2xl border border-forest-900/20 px-4 py-3 bg-white" />
          <input placeholder="Email (opțional)" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full rounded-2xl border border-forest-900/20 px-4 py-3 bg-white" />
          <textarea placeholder="Mesaj (opțional)" value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="w-full rounded-2xl border border-forest-900/20 px-4 py-3 bg-white" />
          <label className="flex gap-2 text-sm"><input type="checkbox" checked={form.anonymous} onChange={e => setForm({...form, anonymous: e.target.checked})} /> Donație anonimă</label>
          <button data-testid="donate-submit" className="w-full bg-tucan text-white rounded-full py-3 font-semibold">Donează {amount} RON</button>
          <p className="text-xs text-muted">Plățile online vor fi disponibile în curând. Vei fi contactat pentru finalizare.</p>
        </form>
      )}
    </div>
  );
}

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/contact", form); setSent(true); toast.success("Mesaj trimis!"); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
      <h1 className="font-serif text-4xl lg:text-6xl text-forest-900 tracking-tight">Contact</h1>
      <p className="mt-4 text-muted">Scrie-ne. Revenim cât de repede putem.</p>
      <div className="mt-6 flex flex-wrap gap-3">
        <a href={waLink()} target="_blank" rel="noreferrer" className="bg-[#25D366] text-white rounded-full px-6 py-3 font-semibold flex items-center gap-2"><MessageCircle className="w-4 h-4" /> WhatsApp: +40 741 142 142</a>
        <a href="tel:+40741142142" className="border-2 border-forest-900 text-forest-900 rounded-full px-6 py-3 font-semibold">Sună</a>
      </div>
      {sent ? <p className="mt-8 text-forest-900">Mulțumim! Am primit mesajul tău.</p> : (
        <form onSubmit={submit} className="mt-8 bg-cream-50 rounded-[24px] p-6 space-y-3">
          <input required placeholder="Nume *" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full rounded-2xl border border-forest-900/20 px-4 py-3 bg-white" />
          <input required type="email" placeholder="Email *" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full rounded-2xl border border-forest-900/20 px-4 py-3 bg-white" />
          <input placeholder="Telefon" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full rounded-2xl border border-forest-900/20 px-4 py-3 bg-white" />
          <textarea required placeholder="Mesaj *" value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="w-full rounded-2xl border border-forest-900/20 px-4 py-3 bg-white min-h-[120px]" />
          <button data-testid="contact-submit" className="w-full bg-tucan text-white rounded-full py-3 font-semibold">Trimite</button>
        </form>
      )}
    </div>
  );
}

export function FAQ() {
  const [faqs, setFaqs] = useState([]);
  useEffect(() => { api.get("/faqs").then(r => setFaqs(r.data)).catch(() => {}); }, []);
  return (
    <div className="max-w-3xl mx-auto px-4 py-12 lg:py-16">
      <h1 className="font-serif text-4xl lg:text-6xl text-forest-900 tracking-tight">Întrebări frecvente</h1>
      <Accordion type="single" collapsible className="mt-10">
        {faqs.map((f, i) => (
          <AccordionItem key={f.id} value={`f${i}`} className="border-b border-forest-900/10">
            <AccordionTrigger className="text-left font-serif text-lg text-forest-900 py-5">{f.question}</AccordionTrigger>
            <AccordionContent className="text-muted leading-relaxed pb-5">{f.answer}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

export function NotFound() {
  return (
    <div className="max-w-2xl mx-auto px-4 py-24 text-center">
      <h1 className="font-serif text-5xl text-forest-900">Se pare că ai ieșit puțin de pe traseu.</h1>
      <p className="mt-4 text-muted">Întoarce-te la Habitat sau descoperă animalele care te așteaptă.</p>
      <Link to="/" className="mt-6 inline-block bg-tucan text-white rounded-full px-6 py-3 font-semibold">Înapoi la pagina principală</Link>
    </div>
  );
}

export function Placeholder({ title }) {
  return (
    <div className="max-w-2xl mx-auto px-4 py-16 text-center">
      <h1 className="font-serif text-4xl text-forest-900">{title}</h1>
      <p className="mt-4 text-muted">Această secțiune este în curs de finalizare. Textele juridice și conținutul complet vor fi disponibile în curând.</p>
      <Link to="/" className="mt-6 inline-block text-forest-900 font-semibold underline">Înapoi acasă</Link>
    </div>
  );
}
